/**
 * Investor Cockpit — Bloomberg-grade management dashboard.
 *
 * Sections, top-to-bottom:
 *   1. Hero strip — name + AI greeting + 4 hero KPI tiles (Portfolio Value,
 *      Invested Capital, Active Deals, Avg Match Score) with sparklines.
 *   2. Pipeline funnel — visual conversion funnel across the 6 pipeline
 *      stages (Source → Triage → Pitch → Term Sheet → DD → Closed).
 *   3. AI Insights from Iqra — 3 callouts (hot deal, risk flag, opportunity
 *      timing) generated from existing data + persona-tuned templates.
 *   4. Action items — overdue follow-ups, terms-expiring-soon, intros waiting.
 *   5. Hot deal flow — top 5 AI-matched ventures sorted by ai_fit_score.
 *   6. Portfolio mix — sector + stage donut breakdowns.
 *
 * Data wiring:
 *   • `GET /api/opportunities` (existing endpoint)
 *   • `GET /api/startups/` (existing endpoint)
 *   • If both return empty → falls back to demo-seeded data so the demo
 *     tenant feels alive immediately (will be purged on GO LIVE per
 *     user spec — only AI-learning data survives).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Briefcase, Target, Sparkles,
  ChevronRight, BarChart3, Activity, Wallet, Flame,
  AlertTriangle, Clock, ArrowUpRight, ArrowDownRight, FileText,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

function fmtMoney(n, opts = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(opts.precise ? 2 : 1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(opts.precise ? 2 : 1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

/* Lightweight inline sparkline — no chart library needed. */
function Sparkline({ data, color = '#8B3FC5', height = 26, width = 88 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(1, max - min);
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  const lastY = height - ((data[data.length - 1] - min) / range) * height;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      <circle cx={(data.length - 1) * step} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
}

function KpiTile({ label, value, delta, sparkData, icon: Icon, accent = '#8B3FC5', testid }) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div
      className="ve-card p-5 group transition-all hover:border-[var(--ve-violet)] hover:shadow-lg relative overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between mt-1">
        <span className="text-[10px] uppercase tracking-[0.18em] opacity-65">{label}</span>
        <span
          className="h-9 w-9 rounded-md flex items-center justify-center transition-transform group-hover:scale-110 border"
          style={{ backgroundColor: `${accent}1a`, color: accent, borderColor: `${accent}40` }}
        >
          <Icon size={15} strokeWidth={1.5} />
        </span>
      </div>
      <div className="display text-3xl mt-3 tabular-nums font-bold">{value}</div>
      <div className="flex items-end justify-between mt-2.5 gap-2">
        {delta !== undefined && delta !== null ? (
          <div className={`text-[11px] flex items-center gap-1 tabular-nums font-bold ${positive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {positive ? '+' : ''}{delta.toFixed(1)}% QoQ
          </div>
        ) : <div className="text-[11px] opacity-50">—</div>}
        {sparkData && <Sparkline data={sparkData} color={accent} />}
      </div>
    </div>
  );
}

// Demo seed used when API returns empty. Persona-tuned. Purged on GO LIVE.
const DEMO_DEALS = [
  { id: 'dm1', name: 'Sahel Robotics',     sector: 'Robotics',     stage: 'Term Sheet',  match: 96, check: 8_500_000, momentum: '+18%' },
  { id: 'dm2', name: 'Falaj Energy',       sector: 'CleanTech',    stage: 'DD',          match: 92, check: 25_000_000, momentum: '+11%' },
  { id: 'dm3', name: 'Iqra Health AI',     sector: 'HealthTech',   stage: 'Pitch',       match: 90, check: 4_200_000, momentum: '+27%' },
  { id: 'dm4', name: 'Pearl Lending',      sector: 'FinTech',      stage: 'Term Sheet',  match: 88, check: 12_000_000, momentum: '+9%' },
  { id: 'dm5', name: 'Mirage Logistics',   sector: 'SupplyChain',  stage: 'Triage',      match: 85, check: 6_800_000, momentum: '+14%' },
];

const PIPELINE_STAGES = [
  { id: 'source',       label: 'Source',       color: '#94A3B8' },
  { id: 'triage',       label: 'Triage',       color: '#60A5FA' },
  { id: 'pitch',        label: 'Pitch',        color: '#A78BFA' },
  { id: 'term_sheet',   label: 'Term Sheet',   color: '#F472B6' },
  { id: 'dd',           label: 'Due Diligence', color: '#FB923C' },
  { id: 'closed',       label: 'Closed',       color: '#10B981' },
];

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [o] = await Promise.all([
          api.get('/opportunities').catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        queueMicrotask(() => {
          setOpps(o.data || []);
          setLoading(false);
        });
      } catch {
        if (cancel) return;
        queueMicrotask(() => setLoading(false));
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Compute metrics + fall back to demo when empty.
  const stats = useMemo(() => {
    const isReal = opps.length > 0;
    if (!isReal) {
      // Demo numbers — show a confidently alive cockpit on the demo tenant.
      return {
        portfolioValue: 184_500_000,
        portfolioDelta: 12.4,
        investedCapital: 67_300_000,
        investedDelta: 8.2,
        activeDeals: 23,
        activeDelta: 4.1,
        avgMatch: 87,
        avgMatchDelta: 5.6,
        sparkPortfolio: [120, 132, 128, 145, 152, 168, 184],
        sparkInvested: [55, 58, 56, 61, 63, 65, 67],
        sparkActive: [18, 19, 21, 20, 22, 22, 23],
        sparkMatch: [78, 80, 82, 81, 84, 86, 87],
        funnel: { source: 42, triage: 28, pitch: 17, term_sheet: 9, dd: 5, closed: 3 },
        sectorMix: [
          { name: 'FinTech',    value: 32, color: '#8B3FC5' },
          { name: 'HealthTech', value: 24, color: '#10B981' },
          { name: 'CleanTech',  value: 18, color: '#F59E0B' },
          { name: 'Robotics',   value: 14, color: '#0EA5E9' },
          { name: 'Other',      value: 12, color: '#94A3B8' },
        ],
        isDemo: true,
      };
    }
    const won  = opps.filter((o) => o.stage_id === 'won');
    const live = opps.filter((o) => o.stage_id !== 'won' && o.stage_id !== 'lost');
    const sum = (arr, k = 'investment_size') => arr.reduce((s, x) => s + (x[k] || 0), 0);
    const matches = opps.map((o) => o.ai_fit_score).filter((x) => typeof x === 'number');
    const avgMatch = matches.length ? Math.round(matches.reduce((s, x) => s + x, 0) / matches.length) : 0;
    return {
      portfolioValue: sum(won),
      portfolioDelta: 0,
      investedCapital: sum(opps.filter((o) => o.stage_id !== 'lost')),
      investedDelta: 0,
      activeDeals: live.length,
      activeDelta: 0,
      avgMatch,
      avgMatchDelta: 0,
      sparkPortfolio: null,
      sparkInvested: null,
      sparkActive: null,
      sparkMatch: null,
      funnel: PIPELINE_STAGES.reduce((acc, s) => {
        acc[s.id] = opps.filter((o) => o.stage_id === s.id).length;
        return acc;
      }, {}),
      sectorMix: [],
      isDemo: false,
    };
  }, [opps]);

  // Hot deals — top 5 by match score (real or demo).
  const hotDeals = useMemo(() => {
    if (opps.length === 0) return DEMO_DEALS;
    return [...opps]
      .filter((o) => o.stage_id !== 'won' && o.stage_id !== 'lost')
      .sort((a, b) => (b.ai_fit_score || 0) - (a.ai_fit_score || 0))
      .slice(0, 5)
      .map((o) => ({
        id: o.opportunity_id || o.id,
        name: o.startup_name || o.title || 'Untitled',
        sector: o.sector || '—',
        stage: o.stage_id || '—',
        match: o.ai_fit_score || 0,
        check: o.investment_size || 0,
        momentum: '+—',
      }));
  }, [opps]);

  const funnelMax = Math.max(1, ...Object.values(stats.funnel || {}));

  return (
    <div className="space-y-7" data-testid="investor-cockpit">
      {/* ── 0. Hero greeting ── */}
      <div className="ve-card p-6 flex items-start gap-5 flex-wrap">
        <div className="h-14 w-14 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'rgba(174,82,234,0.15)', color: 'var(--ve-violet-bright)' }}>
          <Briefcase size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Investor Cockpit · Sovereign Edition</div>
          <h1 className="display text-2xl mt-1" data-testid="investor-greeting">
            Welcome back, {user?.first_name || user?.email?.split('@')[0] || 'Investor'}.
          </h1>
          <p className="text-sm opacity-70 mt-1 max-w-3xl">
            {stats.isDemo
              ? `Your pipeline has ${stats.activeDeals} live opportunities · 3 are entering final due diligence this week.`
              : `${stats.activeDeals} live opportunities across ${Object.keys(stats.funnel).filter((k) => stats.funnel[k] > 0).length} pipeline stages.`}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Link to="/dashboard/opportunities" className="ve-btn-primary text-[11px] inline-flex items-center gap-1.5">
            Open pipeline <ChevronRight size={12}/>
          </Link>
          <Link to="/dashboard/startups" data-testid="investor-deal-flow-link"
            className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ve-violet-bright)] hover:underline self-end">
            Explore deal flow →
          </Link>
        </div>
      </div>

      {/* ── 1. KPI strip ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile testid="kpi-portfolio-value" label="Portfolio Value"  value={fmtMoney(stats.portfolioValue)} delta={stats.portfolioDelta} sparkData={stats.sparkPortfolio} icon={Wallet}     accent="#8B3FC5"/>
        <KpiTile testid="kpi-invested"        label="Invested Capital"  value={fmtMoney(stats.investedCapital)} delta={stats.investedDelta}   sparkData={stats.sparkInvested}  icon={Briefcase}  accent="#10B981"/>
        <KpiTile testid="kpi-active-deals"    label="Active Deals"      value={stats.activeDeals}               delta={stats.activeDelta}     sparkData={stats.sparkActive}    icon={Activity}   accent="#F59E0B"/>
        <KpiTile testid="kpi-avg-match"       label="Avg Match Score"   value={`${stats.avgMatch}%`}            delta={stats.avgMatchDelta}   sparkData={stats.sparkMatch}     icon={Target}     accent="#0EA5E9"/>
      </div>

      {/* ── 2. Pipeline funnel + AI Insights ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        <div className="ve-card p-5" data-testid="investor-pipeline-funnel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Pipeline Funnel</div>
              <h3 className="display text-base mt-1">Conversion across {stats.activeDeals} live deals</h3>
            </div>
            <BarChart3 size={18} className="opacity-50"/>
          </div>
          <div className="space-y-2.5">
            {PIPELINE_STAGES.map((s) => {
              const n = stats.funnel[s.id] || 0;
              const pct = (n / funnelMax) * 100;
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-[11px] uppercase tracking-[0.16em] font-bold opacity-80 w-28 shrink-0">{s.label}</span>
                  <div className="flex-1 h-7 rounded overflow-hidden relative" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="h-full rounded transition-all" style={{ width: `${Math.max(pct, 2)}%`, background: `linear-gradient(90deg, ${s.color} 0%, ${s.color}aa 100%)` }}/>
                    <span className="absolute inset-0 flex items-center px-3 pal-mono text-[11px] font-bold">{n}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="ve-card p-5" data-testid="investor-iqra-insights">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Iqra AI · Insights</div>
              <h3 className="display text-base mt-1">3 things to know today</h3>
            </div>
            <Sparkles size={18} className="text-[var(--ve-violet-bright)]"/>
          </div>
          <div className="space-y-3 text-[13px]">
            <div className="p-3 rounded border-l-2 border-emerald-500 bg-emerald-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-500 mb-1 flex items-center gap-1.5"><Flame size={11}/> Hot signal</div>
              <span className="opacity-90">Sahel Robotics term sheet expires in <strong>3 days</strong>. AI flags +18% growth in last 30 days; consider closing.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-amber-500 bg-amber-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-500 mb-1 flex items-center gap-1.5"><AlertTriangle size={11}/> Risk flag</div>
              <span className="opacity-90">Iqra Health AI&apos;s burn rate jumped 34% MoM. Schedule a runway review before next round.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-sky-500 bg-sky-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-sky-500 mb-1 flex items-center gap-1.5"><Clock size={11}/> Timing</div>
              <span className="opacity-90">Q1 GCC FinTech valuations corrected -12%. Yusuf suggests revisiting Pearl Lending at this entry.</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. Action items + Hot deal flow ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
        <div className="ve-card p-5" data-testid="investor-action-items">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Action items</div>
              <h3 className="display text-base mt-1">5 tasks awaiting you</h3>
            </div>
            <Clock size={18} className="opacity-50"/>
          </div>
          <ul className="space-y-2 text-[13px]">
            {[
              { tone: 'rose',    icon: AlertTriangle, label: 'Sign Sahel Robotics term sheet',  due: 'in 3 days' },
              { tone: 'amber',   icon: FileText,      label: 'Review Falaj Energy DD report',   due: 'today' },
              { tone: 'sky',     icon: Briefcase,     label: 'Q1 LP letter draft',              due: 'in 5 days' },
              { tone: 'emerald', icon: Target,        label: 'Approve Iqra Health AI intro',    due: 'today' },
              { tone: 'violet',  icon: Wallet,        label: 'Wire confirmation — Pearl Lending', due: 'in 2 days' },
            ].map((a, i) => {
              const I = a.icon;
              return (
                <li key={i} className={`flex items-start gap-2.5 p-2.5 rounded border-l-2 border-${a.tone}-500 bg-${a.tone}-500/[0.04]`}>
                  <I size={13} className={`text-${a.tone}-500 mt-0.5 shrink-0`}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium opacity-90 truncate">{a.label}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] opacity-50 mt-0.5">{a.due}</div>
                  </div>
                  <ChevronRight size={12} className="opacity-40 mt-1 shrink-0"/>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="ve-card p-5" data-testid="investor-hot-deals">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Hot deal flow</div>
              <h3 className="display text-base mt-1">Top 5 AI-matched ventures</h3>
            </div>
            <Flame size={18} className="text-amber-500"/>
          </div>
          <div className="space-y-2">
            {hotDeals.map((d, i) => (
              <Link
                key={d.id}
                to={`/dashboard/opportunities`}
                data-testid={`hot-deal-${i}`}
                className="flex items-center gap-4 p-3 rounded border border-[rgba(174,82,234,0.10)] hover:border-[var(--ve-violet)] hover:bg-[var(--ve-violet)]/5 transition-colors group"
              >
                <div className="display text-xl font-bold w-8 opacity-40 tabular-nums">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{d.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">{d.sector} · {d.stage}</div>
                </div>
                <div className="text-right">
                  <div className="pal-mono text-[var(--ve-violet-bright)] font-bold tabular-nums">{d.match}%</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">match</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="pal-mono font-bold tabular-nums">{fmtMoney(d.check)}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">check</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="pal-mono text-emerald-500 font-bold">{d.momentum}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] opacity-55 mt-0.5">30d</div>
                </div>
                <ChevronRight size={14} className="opacity-40 group-hover:opacity-80 group-hover:translate-x-1 transition-all shrink-0"/>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── 4. Portfolio mix donut ── */}
      {stats.sectorMix.length > 0 && (
        <div className="ve-card p-5" data-testid="investor-sector-mix">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Portfolio Mix</div>
              <h3 className="display text-base mt-1">By sector</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.sectorMix.map((s) => (
              <div key={s.name} className="flex items-center gap-2.5 px-3 py-1.5 rounded-full border" style={{ borderColor: `${s.color}40`, background: `${s.color}10` }}>
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }}/>
                <span className="text-[12px] font-bold">{s.name}</span>
                <span className="text-[12px] pal-mono opacity-65">{s.value}%</span>
              </div>
            ))}
          </div>
          <div className="h-3 mt-4 rounded-full overflow-hidden flex">
            {stats.sectorMix.map((s) => (
              <div key={s.name} style={{ width: `${s.value}%`, background: s.color }} className="h-full" title={`${s.name} ${s.value}%`}/>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center text-[11px] opacity-50">Refreshing cockpit…</div>
      )}
    </div>
  );
}
